/**
 * WeatherTab v2 — Multi-Factor OLS Regression Sales Prediction
 *
 * Model: Ordinary Least Squares (Normal Equations) — learns from YOUR actual data
 * Features (7):
 *   β0  bias
 *   β1  sin(2π·DOW/7)   — cyclic day-of-week (avoids Mon=0, Sun=6 discontinuity)
 *   β2  cos(2π·DOW/7)
 *   β3  is_peak_day     — 1 if Thu/Fri/Sat/Sun (food-truck peak days)
 *   β4  temp_comfort    — Gaussian bell peaking at 73°F, → 0 at extremes
 *   β5  precip_impact   — log(1 + inches×10), negative when β5 < 0
 *   β6  wind_impact     — mph / 35, capped 0–1
 *
 * Training window : 60 days of matched Square sales + Open-Meteo daily weather
 * Forecast horizon: 7 days (Open-Meteo free forecast API)
 * Quality metrics : R², RMSE, per-feature importance
 * Weather source  : Open-Meteo (CORS-friendly, 100% free, no API key)
 *   Forecast API  : api.open-meteo.com/v1/forecast  ?past_days=60&forecast_days=7
 *   Includes daily: weather_code, temperature_2m_max/min, precipitation_sum,
 *                   wind_speed_10m_max, sunshine_duration, precipitation_probability_max
 * Location        : NW Arkansas — 36.37°N, 94.21°W
 */

import React, { useState, useEffect, useCallback } from 'react';
import { fmt$ } from '../../../utils/formatUtils';
import { tzDate } from '../../../utils/cstUtils';
import { decodeWMO, isoDate, fetchHistoricalSales } from '../squareDashboardUtils';

// ─── Location ─────────────────────────────────────────────────────────────────
const LAT = 36.37;
const LON = -94.21;

const WEATHER_URL =
  `https://api.open-meteo.com/v1/forecast` +
  `?latitude=${LAT}&longitude=${LON}` +
  `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,` +
  `wind_speed_10m_max,wind_gusts_10m_max,sunshine_duration,precipitation_probability_max` +
  `&current=temperature_2m,apparent_temperature,weather_code,precipitation,` +
  `relative_humidity_2m,wind_speed_10m,is_day,wind_gusts_10m` +
  `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch` +
  `&timezone=America%2FChicago&past_days=60&forecast_days=7`;

const DAY_NAMES      = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DAY_NAMES_LONG = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const CAT_META = {
  clear:  { label:'Clear / Sunny', color:'#f59e0b', emoji:'☀️'  },
  cloudy: { label:'Cloudy',        color:'#64748b', emoji:'⛅'  },
  rainy:  { label:'Rainy',         color:'#3b82f6', emoji:'🌧️' },
  stormy: { label:'Stormy',        color:'#7c3aed', emoji:'⛈️' },
  snowy:  { label:'Snowy',         color:'#0ea5e9', emoji:'❄️'  },
  foggy:  { label:'Foggy',         color:'#9ca3af', emoji:'🌫️' },
};

// ─── Date helpers (local-only) ───────────────────────────────────────────────
function parseIso(s) {
  const [y,m,d] = s.split('-').map(Number);
  return new Date(y, m-1, d);
}

// Fetch N days of daily sales — parallel within 14-day chunks
// ─── Feature Engineering ─────────────────────────────────────────────────────
/**
 * Extract 7 regression features from a day's weather.
 *
 * Feature design rationale (food-truck / outdoor dining):
 *  sin/cos DOW  — captures weekly cycle without ordinal discontinuity
 *  is_peak_day  — Thu-Sun drives disproportionate foot traffic for food trucks
 *  temp_comfort — Gaussian bell @ 73°F; perfect weather=1.0, ±40°F from peak → ~0
 *  precip_log   — log(1 + precip*10): even 0.1" rain hurts; diminishing returns
 *  wind_norm    — customers avoid food trucks in gusty weather; linear 0–35 mph
 */
function makeFeatures(date, tempMax, precipInches, windMph) {
  const dow    = date.getDay();
  const sinDow = Math.sin(2 * Math.PI * dow / 7);
  const cosDow = Math.cos(2 * Math.PI * dow / 7);
  // Thu=4, Fri=5, Sat=6, Sun=0
  const isPeak     = (dow === 4 || dow === 5 || dow === 6 || dow === 0) ? 1 : 0;
  // Gaussian comfort curve: peaks at 73°F, half-max at ~54°F & ~92°F
  const tComfort   = Math.exp(-Math.pow(tempMax - 73, 2) / 350);
  // Log precipitation
  const precipLog  = Math.log1p((precipInches || 0) * 10);
  // Wind normalized to [0,1]
  const windNorm   = Math.min((windMph || 0) / 35, 1);
  return [1, sinDow, cosDow, isPeak, tComfort, precipLog, windNorm];
}

const FEATURE_NAMES = ['Baseline','DOW-sin','DOW-cos','Peak day','Temp comfort','Precipitation','Wind speed'];

// ─── OLS Matrix Math ─────────────────────────────────────────────────────────
function transpose(A) {
  const m = A.length, n = A[0].length;
  return Array.from({length: n}, (_, j) => Array.from({length: m}, (_, i) => A[i][j]));
}
function matMul(A, B) {
  const p = A.length, r = B[0].length;
  return Array.from({length: p}, (_, i) =>
    Array.from({length: r}, (_, k) =>
      A[i].reduce((s, _, j) => s + A[i][j] * B[j][k], 0)));
}

/**
 * Solve Ax = b via Gauss-Jordan elimination with partial pivoting.
 * Returns solution vector x, or null if singular.
 */
function solveLinear(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    if (Math.abs(M[col][col]) < 1e-10) return null;
    const piv = M[col][col];
    M[col] = M[col].map(v => v / piv);
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const f = M[row][col];
      M[row] = M[row].map((v, j) => v - f * M[col][j]);
    }
  }
  return M.map(row => row[n]);
}

/**
 * Fit OLS linear regression.
 * @param {number[][]} X  — m × n feature matrix
 * @param {number[]}   y  — m target values
 * @returns {{ beta, r2, rmse, residuals, yPred, nSamples }} or null
 */
function fitOLS(X, y) {
  const m = X.length, n = X[0].length;
  if (m < n + 2) return null;

  const Xt   = transpose(X);
  const XtX  = matMul(Xt, X);
  const Xty  = matMul(Xt, y.map(v => [v])).map(r => r[0]);
  const beta = solveLinear(XtX, Xty);
  if (!beta) return null;

  const yPred = X.map(row => row.reduce((s, v, j) => s + v * beta[j], 0));
  const yMean = y.reduce((s, v) => s + v, 0) / m;
  let ssRes = 0, ssTot = 0;
  y.forEach((yi, i) => {
    ssRes += (yi - yPred[i]) ** 2;
    ssTot += (yi - yMean) ** 2;
  });
  const r2        = ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0;
  const rmse      = Math.sqrt(ssRes / m);
  const residuals = y.map((yi, i) => yi - yPred[i]);
  return { beta, r2, rmse, residuals, yPred, nSamples: m };
}

/**
 * Predict revenue for a future day using fitted model.
 * Returns { pred, lo, hi } with 80% prediction interval.
 */
function predictDay(model, date, tempMax, precipInches, windMph) {
  const { beta, rmse } = model;
  const f    = makeFeatures(date, tempMax, precipInches, windMph);
  const pred = Math.max(0, f.reduce((s, v, j) => s + v * beta[j], 0));
  const lo   = Math.max(0, pred - 1.28 * rmse);  // 80% interval
  const hi   = pred + 1.28 * rmse;
  return { pred, lo, hi };
}

// ─── Insight Generator ────────────────────────────────────────────────────────
function buildInsights(model, forecastDays) {
  const insights = [];
  if (!model) return insights;
  const { beta, r2 } = model;

  if (r2 >= 0.5) {
    insights.push({ type:'positive', icon:'🎯',
      text:`Strong model fit: explaining ${(r2*100).toFixed(0)}% of your daily sales variance (R²=${r2.toFixed(2)}). Predictions are grounded in your actual weather-sales patterns.`});
  } else if (r2 >= 0.3) {
    insights.push({ type:'info', icon:'📊',
      text:`Moderate model fit (R²=${r2.toFixed(2)}). Weather explains ${(r2*100).toFixed(0)}% of variance — other factors like events, marketing, and seasonality also contribute.`});
  } else {
    insights.push({ type:'caution', icon:'⚠️',
      text:`Low model fit (R²=${r2.toFixed(2)}) — predictions are directionally correct but wide. More operating days with varied weather will sharpen the model.`});
  }

  // Rain impact from coefficient
  if (beta[5] < -10) {
    const rainLoss = Math.abs(beta[5] * Math.log1p(2)); // ~0.2" of rain
    insights.push({ type:'caution', icon:'🌧️',
      text:`Rain is your biggest weather headwind. A typical 0.2" rain day costs ~${fmt$(Math.round(rainLoss))} in revenue vs a dry day. Consider a "Rainy Day Combo" promo.`});
  }

  // Wind impact
  if (beta[6] < -20) {
    insights.push({ type:'caution', icon:'💨',
      text:`Wind significantly hurts sales (β=${Math.round(beta[6])}). Days above 20 mph see measurable drop-off — especially important for outdoor food truck setup.`});
  }

  // Temperature comfort
  if (Math.abs(beta[4]) > 10) {
    insights.push({ type: beta[4] > 0 ? 'positive' : 'caution', icon:'🌡️',
      text:`Temperature comfort has a ${beta[4] > 0 ? 'strong positive' : 'notable'} effect on revenue. Peak performance occurs around 65–80°F. Extreme heat or cold both reduce foot traffic.`});
  }

  // Best upcoming day
  const sorted = [...forecastDays].filter(d => d.result).sort((a,b) => b.result.pred - a.result.pred);
  if (sorted.length > 0) {
    const best = sorted[0];
    insights.push({ type:'positive', icon:'📅',
      text:`Best upcoming day: ${best.dowLong} — forecast ${fmt$(Math.round(best.result.pred))} with ${best.weather.label.toLowerCase()} (${Math.round(best.tempMax)}°F, 💨${Math.round(best.wind)} mph).`});
  }

  // Rain warning
  const rainyUpcoming = forecastDays.filter(d => d.precip > 0.1 || d.precipProb >= 50);
  if (rainyUpcoming.length > 0) {
    insights.push({ type:'caution', icon:'☔',
      text:`Wet days ahead: ${rainyUpcoming.map(d => d.dow).join(', ')} have ≥50% rain chance. Plan staffing and prep volumes conservatively.`});
  }

  // Peak day strength
  if (beta[3] > 20) {
    insights.push({ type:'info', icon:'🚀',
      text:`Thu–Sun consistently outperform Mon–Wed by ~${fmt$(Math.round(beta[3]))}/day in your data. Maximize inventory and staff on peak days.`});
  }

  return insights;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function WeatherTab() {
  const [wxData,      setWxData]      = useState(null);
  const [salesHist,   setSalesHist]   = useState({});
  const [loading,     setLoading]     = useState(true);
  const [loadStep,    setLoadStep]    = useState('');
  const [error,       setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Derived: weather history map from Open-Meteo past_days ─────────────────
  const wxHistory = React.useMemo(() => {
    if (!wxData?.daily) return {};
    const map = {};
    wxData.daily.time.forEach((dateStr, i) => {
      map[dateStr] = {
        wmo:        wxData.daily.weather_code[i],
        tempMax:    wxData.daily.temperature_2m_max[i],
        tempMin:    wxData.daily.temperature_2m_min[i],
        precip:     wxData.daily.precipitation_sum[i]              || 0,
        wind:       wxData.daily.wind_speed_10m_max[i]             || 0,
        gusts:      wxData.daily.wind_gusts_10m_max?.[i]           || 0,
        sunSec:     wxData.daily.sunshine_duration?.[i]            || 0,
        precipProb: wxData.daily.precipitation_probability_max?.[i]|| 0,
        ...decodeWMO(wxData.daily.weather_code[i]),
      };
    });
    return map;
  }, [wxData]);

  // ── Derived: OLS model trained on matched historical days ──────────────────
  const model = React.useMemo(() => {
    const todayStr = isoDate(tzDate(new Date()));
    const paired = Object.entries(salesHist)
      .filter(([d, rev]) => rev !== null && rev > 0 && wxHistory[d] && d < todayStr)
      .map(([d, rev]) => {
        const wx   = wxHistory[d];
        const date = parseIso(d);
        return { dateStr: d, date, rev, feat: makeFeatures(date, wx.tempMax, wx.precip, wx.wind) };
      })
      .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

    if (paired.length < 9) return null;

    const X   = paired.map(p => p.feat);
    const y   = paired.map(p => p.rev);
    const fit = fitOLS(X, y);
    if (!fit) return null;

    fit.trainSamples = paired.map((p, i) => ({
      dateStr: p.dateStr,
      actual:  p.rev,
      pred:    fit.yPred[i],
      resid:   fit.residuals[i],
      wx:      wxHistory[p.dateStr],
    }));
    return fit;
  }, [salesHist, wxHistory]);

  // ── Derived: weather-category correlation table ────────────────────────────
  const corrMap = React.useMemo(() => {
    const map = {};
    Object.entries(salesHist).forEach(([d, rev]) => {
      if (!rev || !wxHistory[d]) return;
      const cat = wxHistory[d].cat;
      if (!map[cat]) map[cat] = { count: 0, total: 0 };
      map[cat].count++;
      map[cat].total += rev;
    });
    Object.values(map).forEach(v => { v.avg = v.total / v.count; });
    return map;
  }, [salesHist, wxHistory]);

  // ── Derived: 7-day forecast predictions ───────────────────────────────────
  const forecastDays = React.useMemo(() => {
    if (!wxData?.daily) return [];
    const todayStr = isoDate(tzDate(new Date()));
    return wxData.daily.time
      .map((dateStr, i) => {
        if (dateStr <= todayStr) return null;
        const wx = wxHistory[dateStr];
        if (!wx) return null;
        const date   = parseIso(dateStr);
        const result = model ? predictDay(model, date, wx.tempMax, wx.precip, wx.wind) : null;
        return {
          dateStr, date,
          dow:      DAY_NAMES[date.getDay()],
          dowLong:  DAY_NAMES_LONG[date.getDay()],
          weather:  wx,
          tempMax:  wx.tempMax,
          tempMin:  wx.tempMin,
          precip:   wx.precip,
          precipProb: wx.precipProb,
          wind:     wx.wind,
          gusts:    wx.gusts,
          result,
        };
      })
      .filter(Boolean)
      .slice(0, 7);
  }, [wxData, wxHistory, model]);

  // ── Derived: ranked days ──────────────────────────────────────────────────
  const rankedDays = React.useMemo(() => {
    const days = Object.entries(salesHist)
      .filter(([d, rev]) => rev && wxHistory[d])
      .map(([d, rev]) => ({ dateStr: d, rev, dow: DAY_NAMES[parseIso(d).getDay()], wx: wxHistory[d] }))
      .sort((a, b) => b.rev - a.rev);
    return { best: days.slice(0, 5), worst: days.slice(-5).reverse() };
  }, [salesHist, wxHistory]);

  const insights = React.useMemo(() => buildInsights(model, forecastDays), [model, forecastDays]);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLoadStep('Fetching 60-day weather data from Open-Meteo…');
      const wxRes = await fetch(WEATHER_URL);
      if (!wxRes.ok) throw new Error(`Open-Meteo error ${wxRes.status}`);
      const wx = await wxRes.json();
      setWxData(wx);

      setLoadStep('Loading 60 days of sales data (this takes ~10 seconds)…');
      const hist = await fetchHistoricalSales(60);
      setSalesHist(hist);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setLoadStep('');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const cur   = wxData?.current;
  const curWX = cur ? decodeWMO(cur.weather_code) : null;
  const maxPred = Math.max(...forecastDays.map(d => d.result?.pred || 0), 1);
  const maxCorr = Math.max(...Object.values(corrMap).map(v => v.avg), 1);
  const totalBetaMag = model ? model.beta.slice(1).reduce((s, v) => s + Math.abs(v), 0) : 1;

  if (loading) {
    return (
      <div className="wx__loading">
        <div className="wx__loading-spinner" />
        <p>{loadStep || 'Loading…'}</p>
        <small style={{color:'#94a3b8', marginTop: 4}}>
          Fetching 60 days of weather + sales data to train the regression model
        </small>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wx__error">
        <span>⚠️ {error}</span>
        <button onClick={loadData} className="wx__retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="wx__root">

      {/* ── Current Conditions ── */}
      {cur && curWX && (
        <div className="wx__current"
          style={{background: cur.is_day
            ? 'linear-gradient(135deg,#0c4a6e,#0369a1)'
            : 'linear-gradient(135deg,#1e1b4b,#1e3a5f)'}}>
          <div className="wx__current-left">
            <div className="wx__current-icon">{curWX.emoji}</div>
            <div className="wx__current-info">
              <div className="wx__current-temp">{Math.round(cur.temperature_2m)}°F</div>
              <div className="wx__current-desc">{curWX.label}</div>
              <div className="wx__current-loc">📍 NW Arkansas</div>
            </div>
          </div>
          <div className="wx__current-details">
            <div className="wx__current-detail">
              <span className="wx__cd-label">Feels Like</span>
              <span className="wx__cd-val">{Math.round(cur.apparent_temperature)}°F</span>
            </div>
            <div className="wx__current-detail">
              <span className="wx__cd-label">Humidity</span>
              <span className="wx__cd-val">{cur.relative_humidity_2m}%</span>
            </div>
            <div className="wx__current-detail">
              <span className="wx__cd-label">Wind</span>
              <span className="wx__cd-val">{Math.round(cur.wind_speed_10m)} mph</span>
            </div>
            <div className="wx__current-detail">
              <span className="wx__cd-label">Gusts</span>
              <span className="wx__cd-val">{Math.round(cur.wind_gusts_10m || 0)} mph</span>
            </div>
            <div className="wx__current-detail">
              <span className="wx__cd-label">Precip</span>
              <span className="wx__cd-val">{(cur.precipitation || 0).toFixed(2)}"</span>
            </div>
          </div>
          {model && (
            <div className="wx__current-badge">
              <div className="wx__current-badge-label">OLS Model</div>
              <div className="wx__current-badge-mode">R² = {model.r2.toFixed(2)}</div>
              <div style={{fontSize:'.65rem', opacity:.75, marginTop:2}}>
                {model.nSamples} training days
              </div>
            </div>
          )}
          {lastUpdated && (
            <div className="wx__current-updated">Updated {lastUpdated.toLocaleTimeString()}</div>
          )}
        </div>
      )}

      {/* ── OLS Model Summary ── */}
      {model && (
        <div className="wx__section">
          <div className="wx__section-title">🧮 Regression Model — Trained on Your Own Data</div>
          <div className="wx__section-sub">
            Ordinary Least Squares fitted on {model.nSamples} matched sales+weather days &nbsp;·&nbsp;
            R² = {model.r2.toFixed(3)} &nbsp;·&nbsp;
            RMSE = {fmt$(Math.round(model.rmse))} &nbsp;·&nbsp;
            80% prediction interval = ±{fmt$(Math.round(1.28 * model.rmse))}
          </div>

          <div className="wx__model-grid">
            {model.beta.map((b, i) => {
              if (i === 0) return null; // skip bias row in display
              const pct  = totalBetaMag > 0 ? Math.abs(b) / totalBetaMag * 100 : 0;
              const barW = Math.min(pct * 2.5, 100);
              return (
                <div key={i} className="wx__model-row">
                  <span className="wx__model-name">{FEATURE_NAMES[i]}</span>
                  <div className="wx__model-bar-wrap">
                    <div className="wx__model-bar"
                      style={{width:`${barW}%`, background: b > 0 ? '#10b981' : '#ef4444'}} />
                  </div>
                  <span className={`wx__model-coef ${b > 0 ? 'wx__coef--pos' : 'wx__coef--neg'}`}>
                    {b > 0 ? '+' : ''}{fmt$(Math.round(b))}
                  </span>
                  <span className="wx__model-dir">{b > 0 ? '▲' : '▼'}</span>
                </div>
              );
            })}
          </div>
          <p className="wx__model-note">
            Each coefficient shows the dollar impact of that feature on daily revenue.
            These weights were computed entirely from your actual {model.nSamples} days of Square sales data — not hardcoded estimates.
          </p>
        </div>
      )}

      {!model && (
        <div className="wx__section" style={{textAlign:'center', padding:'32px 20px'}}>
          <div style={{fontSize:'2rem', marginBottom:8}}>🧮</div>
          <div style={{fontWeight:700, marginBottom:6}}>Regression model training…</div>
          <div style={{fontSize:'.85rem', color:'var(--sq-text-3)'}}>
            Need ≥9 operating days with matched weather data to fit the OLS model.
            Once enough data is collected, predictions will appear automatically.
          </div>
        </div>
      )}

      {/* ── 7-Day Forecast ── */}
      <div className="wx__section">
        <div className="wx__section-title">🔮 7-Day Sales Forecast</div>
        <div className="wx__section-sub">
          {model
            ? `OLS regression predictions · 80% interval ±${fmt$(Math.round(1.28 * model.rmse))} · trained on ${model.nSamples} days`
            : 'Collecting training data — predictions appear once ≥9 operating days are available'}
        </div>
        <div className="wx__forecast-grid">
          {forecastDays.map((day, idx) => {
            const pred   = day.result?.pred || 0;
            const lo     = day.result?.lo   || 0;
            const hi     = day.result?.hi   || 0;
            const barPct = maxPred > 0 ? (pred / maxPred) * 100 : 0;
            const isWindy = day.wind > 20;
            const isRainy = day.precip > 0.1 || day.precipProb >= 50;
            const isHot   = day.tempMax > 95;
            const isCold  = day.tempMax < 40;

            return (
              <div key={day.dateStr}
                className={`wx__forecast-card${idx === 0 ? ' wx__forecast-card--first' : ''}`}>
                <div className="wx__fc-dow">{day.dow}</div>
                <div className="wx__fc-date">
                  {day.date.toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                </div>
                <div className="wx__fc-icon">{day.weather.emoji}</div>
                <div className="wx__fc-cond">{day.weather.label}</div>
                <div className="wx__fc-temps">
                  {Math.round(day.tempMax)}° / {Math.round(day.tempMin)}°
                </div>
                <div className="wx__fc-flags">
                  {isRainy && <span className="wx__flag wx__flag--rain">💧{day.precipProb}%</span>}
                  {isWindy && <span className="wx__flag wx__flag--wind">💨{Math.round(day.wind)}</span>}
                  {isHot   && <span className="wx__flag wx__flag--hot">🌡️Hot</span>}
                  {isCold  && <span className="wx__flag wx__flag--cold">🥶Cold</span>}
                </div>
                <div className="wx__fc-bar-wrap">
                  <div className="wx__fc-bar" style={{width:`${barPct}%`}} />
                </div>
                <div className="wx__fc-amount">{pred > 0 ? fmt$(Math.round(pred)) : '—'}</div>
                {model && pred > 0 && (
                  <div className="wx__fc-range">{fmt$(Math.round(lo))} – {fmt$(Math.round(hi))}</div>
                )}
                {!model && <div className="wx__fc-conf wx__conf--low">No model yet</div>}
              </div>
            );
          })}
          {forecastDays.length === 0 && (
            <div className="wx__empty">Forecast data loading…</div>
          )}
        </div>
      </div>

      {/* ── Weather-Sales Correlation ── */}
      {Object.keys(corrMap).length > 0 && (
        <div className="wx__section">
          <div className="wx__section-title">📊 Avg Revenue by Weather Condition</div>
          <div className="wx__section-sub">
            Raw averages from {Object.values(corrMap).reduce((s,v)=>s+v.count,0)} matched sales+weather days
          </div>
          <div className="wx__corr-grid">
            {Object.entries(corrMap)
              .sort((a, b) => b[1].avg - a[1].avg)
              .map(([cat, data]) => {
                const meta   = CAT_META[cat] || { label: cat, emoji:'🌡️', color:'#64748b' };
                const barPct = maxCorr > 0 ? (data.avg / maxCorr) * 100 : 0;
                return (
                  <div key={cat} className="wx__corr-row">
                    <div className="wx__corr-label">
                      <span className="wx__corr-emoji">{meta.emoji}</span>
                      <span className="wx__corr-name">{meta.label}</span>
                      <span className="wx__corr-count">({data.count}d)</span>
                    </div>
                    <div className="wx__corr-bar-wrap">
                      <div className="wx__corr-bar"
                        style={{width:`${barPct}%`, background: meta.color}} />
                      <span className="wx__corr-amt">{fmt$(Math.round(data.avg))}/day</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Best & Worst Days ── */}
      {(rankedDays.best.length > 0 || rankedDays.worst.length > 0) && (
        <div className="wx__section wx__section--split">
          <div className="wx__ranked-col">
            <div className="wx__section-title">🏆 Best Sales Days (Last 60d)</div>
            <div className="wx__ranked-list">
              {rankedDays.best.map((d, i) => (
                <div key={d.dateStr} className="wx__ranked-row wx__ranked-row--best">
                  <span className="wx__ranked-medal">{['🥇','🥈','🥉','4th','5th'][i]}</span>
                  <span className="wx__ranked-emoji">{d.wx.emoji}</span>
                  <div className="wx__ranked-info">
                    <div className="wx__ranked-date">
                      {d.dow} · {parseIso(d.dateStr).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                    </div>
                    <div className="wx__ranked-cond">
                      {d.wx.label} · {Math.round(d.wx.tempMax)}°F · 💨{Math.round(d.wx.wind)} mph
                    </div>
                  </div>
                  <span className="wx__ranked-rev">{fmt$(Math.round(d.rev))}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="wx__ranked-col">
            <div className="wx__section-title">📉 Lowest Sales Days (Last 60d)</div>
            <div className="wx__ranked-list">
              {rankedDays.worst.map((d, i) => (
                <div key={d.dateStr} className="wx__ranked-row wx__ranked-row--worst">
                  <span className="wx__ranked-num">{i+1}.</span>
                  <span className="wx__ranked-emoji">{d.wx.emoji}</span>
                  <div className="wx__ranked-info">
                    <div className="wx__ranked-date">
                      {d.dow} · {parseIso(d.dateStr).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                    </div>
                    <div className="wx__ranked-cond">
                      {d.wx.label} · {Math.round(d.wx.tempMax)}°F · 💧{d.wx.precip.toFixed(2)}"
                    </div>
                  </div>
                  <span className="wx__ranked-rev wx__ranked-rev--low">{fmt$(Math.round(d.rev))}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Model Accuracy: Actual vs Predicted (last 14 training days) ── */}
      {model && model.trainSamples.length >= 3 && (
        <div className="wx__section">
          <div className="wx__section-title">🔬 Backtesting — Actual vs Predicted (Last 14 Days)</div>
          <div className="wx__section-sub">
            How well the model explains days it was trained on. Green = within 20%, orange = wider miss.
          </div>
          <div className="wx__resid-grid">
            {model.trainSamples.slice(-14).reverse().map(s => {
              const errPct = s.actual > 0 ? ((s.actual - s.pred) / s.actual * 100) : 0;
              const good   = Math.abs(errPct) < 20;
              return (
                <div key={s.dateStr} className={`wx__resid-row ${good ? '' : 'wx__resid-row--miss'}`}>
                  <span className="wx__resid-emoji">{s.wx.emoji}</span>
                  <div className="wx__resid-info">
                    <span className="wx__resid-date">
                      {DAY_NAMES[parseIso(s.dateStr).getDay()]} ·{' '}
                      {parseIso(s.dateStr).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                    </span>
                    <span className="wx__resid-cond">
                      {s.wx.label} · {Math.round(s.wx.tempMax)}°F · 💨{Math.round(s.wx.wind)} mph
                    </span>
                  </div>
                  <div className="wx__resid-vals">
                    <span className="wx__resid-actual">Actual: {fmt$(Math.round(s.actual))}</span>
                    <span className="wx__resid-pred">Model: {fmt$(Math.round(s.pred))}</span>
                  </div>
                  <span className={`wx__resid-err ${errPct >= 0 ? 'wx__resid-err--over' : 'wx__resid-err--under'}`}>
                    {errPct >= 0 ? '+' : ''}{errPct.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Insights ── */}
      {insights.length > 0 && (
        <div className="wx__section">
          <div className="wx__section-title">🧠 Intelligence Insights</div>
          <div className="wx__insights-list">
            {insights.map((ins, i) => (
              <div key={i} className={`wx__insight wx__insight--${ins.type}`}>
                <span className="wx__insight-icon">{ins.icon}</span>
                <span className="wx__insight-text">{ins.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="wx__footer-note">
        <strong>📐 Methodology:</strong> A 7-feature Ordinary Least Squares regression is fitted
        to 60 days of your actual Square sales matched with Open-Meteo weather data.
        Features use cyclic day-of-week encoding, a Gaussian temperature comfort curve (peak 73°F),
        log-transformed precipitation (diminishing marginal effect), and normalized wind speed.
        80% prediction intervals = ±1.28 × RMSE.
        Weather from <a href="https://open-meteo.com" target="_blank" rel="noopener noreferrer">Open-Meteo</a> (free, no API key).
        {model && ` Model: RMSE=${fmt$(Math.round(model.rmse))}, R²=${model.r2.toFixed(3)}, n=${model.nSamples}.`}
      </div>

    </div>
  );
}
